import { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'

import { db } from '../FirebaseInit'
import { useToast } from '../context/ToastContext'

/**
 * useTickets
 *
 * Subscribes to the tickets collection scoped by role. Admins read every
 * ticket ordered by `createdAt` desc; customers read only tickets whose
 * `customerId` matches their claim. Client scoping mirrors the Firestore
 * security rules so a query never asks for tickets the rules would deny.
 *
 * @param {Object} params
 * @param {boolean} params.admin Whether the current user is an admin.
 * @param {string} [params.customerId] The current customer's id.
 * @returns {{ data: Array, loading: boolean, error: (Error|null) }}
 */
const useTickets = ({ admin, customerId }) => {
  const { showToast } = useToast()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Customers without a resolved customerId have nothing to subscribe to.
    if (!admin && !customerId) {
      setData([])
      setLoading(false)
      setError(null)
      return
    }

    setLoading(true)

    const ticketsRef = collection(db, 'tickets')
    const ticketsQuery = admin
      ? query(ticketsRef, orderBy('createdAt', 'desc'))
      : query(ticketsRef, where('customerId', '==', customerId), orderBy('createdAt', 'desc'))

    const unsubscribe = onSnapshot(
      ticketsQuery,
      (snapshot) => {
        setData(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
        setError(null)
        setLoading(false)
      },
      (err) => {
        if (err.code === 'permission-denied') {
          showToast('You don\'t have access to these tickets', 'red')
        } else {
          // Surface other failures (e.g. a missing composite index shows up as
          // failed-precondition) instead of silently rendering an empty list.
          console.error('useTickets query failed:', err)
          showToast('Could not load tickets. Please try again.', 'red')
        }
        setError(err)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [admin, customerId, showToast])

  return { data, loading, error }
}

export default useTickets
